// Auto-redirect authenticated users to their preferred locale (#1116).
//
// On every page load, if the visitor is authenticated AND has a
// `preferences.language` set in their profile, replace the current URL
// with the same path under their preferred locale. Operates entirely
// client-side: reads the cached user object from localStorage so there
// is no extra API round-trip in the critical path.
//
// Pages that should NOT redirect:
//   - Auth flow itself (/login, /register, /reset-password) — the user
//     hasn't necessarily picked a locale yet, and redirecting away
//     mid-form would lose their input.
//   - Unprefixed pages with no /:locale twin (the waitlist unsubscribe
//     link from mail). A locale is otherwise *inserted* into an
//     unprefixed path (/dashboard → /uk/dashboard); for these that
//     lands on a 404.
//
// Escape hatch: append `?no-locale-redirect=1` to the URL to skip the
// redirect (useful for QA testing locale fallbacks).

import { getStoredUser } from './auth.ts';

const SUPPORTED_LANGS = [
  "en", "uk", "pl", "de", "es", "fr", "pt", "it", "ar", "tr",
  "he", "nl", "cs", "ro", "hr", "bg", "sk", "sv", "el", "hu",
];

const AUTH_FLOW_PATHS = ["/login", "/register", "/reset-password", "/logout"];
const UNLOCALIZED_PREFIXES = ["/waitlist/"];

interface StoredUser {
  preferences?: { language?: string | null };
  locale?: string | null;
}

function preferredLocaleOf(user: StoredUser): string | null {
  const lang =
    user.preferences?.language ||
    user.locale ||
    null;
  return lang && SUPPORTED_LANGS.includes(lang) ? lang : null;
}

function currentLocaleOf(pathname: string): string | null {
  // First path segment, e.g. "/uk/dashboard" → "uk".
  const match = pathname.match(/^\/([a-z]{2})(?:\/|$)/);
  if (!match) return null;
  return SUPPORTED_LANGS.includes(match[1]) ? match[1] : null;
}

function isAuthFlowPath(pathname: string): boolean {
  // Strip locale prefix if present.
  const stripped = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "");
  return AUTH_FLOW_PATHS.some((p) => stripped === p || stripped.startsWith(`${p}/`));
}

/**
 * Returns the URL to redirect to, OR null if no redirect is needed.
 * Pure function — exported for unit tests.
 */
export function computeRedirect(
  pathname: string,
  search: string,
  user: StoredUser | null,
): string | null {
  if (!user) return null;
  if (isAuthFlowPath(pathname)) return null;
  if (UNLOCALIZED_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  if (search.includes("no-locale-redirect")) return null;

  const preferred = preferredLocaleOf(user);
  if (!preferred) return null;

  const current = currentLocaleOf(pathname);
  if (current === preferred) return null;

  // Swap or insert the locale segment.
  const rest = current ? pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "") : pathname;
  const newPath = `/${preferred}${rest === "" ? "" : rest}`;
  return newPath || `/${preferred}`;
}

/**
 * Mount the redirect on page load. Entrypoints that render localized
 * content should call this once near the top of their boot — landing,
 * dashboard, marketplace, brands, blog, planner, profile.
 *
 * Auth flow entrypoints (login/register) should NOT call this.
 */
export function enforcePreferredLocale(): void {
  const target = computeRedirect(
    window.location.pathname,
    window.location.search,
    getStoredUser() as StoredUser | null,
  );
  if (target) {
    // `replace` — no extra back-history entry, mimics how the user
    // would have landed if they'd typed the right URL the first time.
    window.location.replace(target + window.location.search + window.location.hash);
  }
}
